import _ from 'lodash'
import Base from '@/resources/Base'
import { RiskLevelEnum } from '@/constants/risk-level.constants'
import { HostStatusEnum } from '@/constants/host-status.constants'
import { CriticalityLevelEnum } from '@/constants/criticality.constants'
import { getUserFriendlyAgentLabel } from '@/utilities/agent.helper'
import { lastNthDays } from '@/utilities/datetime.helper'
import { HOSTMAP_NUM_OF_DAYS } from '@/constants/statistics.constants'

const HOSTMAP_GROUPBY_NONE = ['1', '2', '3']
const NOW_STRING = 'Now'

export default class Hostmap extends Base {
  /**
   * Collection for Agent model classes.
   *
   * @param {*} nodes  Nodes to add to this collection.
   * @param {*} options Options to set on this collection.
   * @param {*} attributes Attributes used for search on this collection.
   *
   **/

  constructor (nodes = [], attributes = {}, options = {}) {
    super('', attributes, options)
    this.nodes = []
    this.links = []
    this.groupBy = options.groupBy || HOSTMAP_GROUPBY_NONE
    this.groupByProperty = options.groupByProperty || undefined
    this.rangeStart = 100
    this.rangeEnd = 100
    this.setDateRangeSteps()
  }

  getDisplayName (agent) {
    if (agent.customHostname !== 'UNNAMED') {
      return agent.customHostname
    }
    return agent.agentLabel
  }

  getAgentProperties (node, agent) {
    return {
      agentId: node,
      name: this.getDisplayName(agent),
      riskLevel: agent.riskLevel,
      state: agent.state,
      criticality: agent.criticality
    }
  }

  setAgentModels (agents) {
    this.agents = agents
  }

  setDateRange (start, end) {
    this.rangeStart = start
    this.rangeEnd = end
  }

  getDateRangeSteps () {
    return this.dateRange
  }

  setDateRangeSteps () {
    let dateOptions = lastNthDays(HOSTMAP_NUM_OF_DAYS)
    let dates = dateOptions.slice().reverse()
    let steps = {}
    dates.forEach((date, idx) => {
      if (idx === 0) {
        steps[0] = date
      } else if (idx === HOSTMAP_NUM_OF_DAYS - 1) {
        steps[100] = NOW_STRING
      } else {
        steps[idx * (100 / (HOSTMAP_NUM_OF_DAYS - 1))] = date
      }
    })
    this.dateRange = steps
  }

  clearAgentPositions (node) {
    // allow to be reset by d3
    node.fx = null
    node.x = null
    node.y = null
    return node
  }

  /**
   * Create node object for d3 consumption
   * group, state & risklevel for color
   *
   * @param {*} agent
   */
  getAgentName (agent) {
    if (!_.isEmpty(this.agents)) {
      let found = _.find(this.agents, { agentId: agent })
      if (!_.isUndefined(found)) {
        return this.getAgentProperties(agent, found)
      }
    }
    return {
      agentId: agent,
      name: getUserFriendlyAgentLabel(agent),
      riskLevel: {
        level: RiskLevelEnum.ZERO,
        label: RiskLevelEnum.properties[RiskLevelEnum.ZERO].title
      },
      state: HostStatusEnum.properties.offline.state,
      criticality: CriticalityLevelEnum.REGULAR
    }
  }

  setAgentNodes (sources, targets) {
    let numOfGroups = this.groupBy.length
    let groupId = 1
    let nodes = _.uniq(sources.concat(targets))

    // create node object for d3 consumption
    // group, state & risklevel for color
    this.nodes = nodes.map((node, idx) => {
      if (groupId === numOfGroups + 1) {
        groupId = 1
      }
      let exists = this.getAgentName(node)
      return _.assignIn({ id: exists.name, group: `${groupId++}` }, exists)
    })

    if (_.isUndefined(this.groupByProperty)) {
      return this.groupByNone(this.nodes)
    } else {
      return this.applyGroupBy(this.groupByProperty, this.nodes)
    }
  }

  setLinks (links) {
    this.links = _.filter(links, node => {
      return node.sourceAgent !== node.targetAgent
    })

    this.links.map(link => {
      return _.merge(link, {
        source: this.getAgentName(link.sourceAgent).name,
        target: this.getAgentName(link.targetAgent).name
      })
    })

    return this.links
  }

  updateAgentNodes (sources, targets) {
    let numOfGroups = this.groupBy.length
    let groupId = 1
    let newNodes = _.uniq(sources.concat(targets))

    // Add node if not found
    // No deletion (will just show without connects)
    newNodes.forEach(agent => {
      let foundNode = _.find(this.nodes, { agentId: agent })

      if (_.isUndefined(foundNode)) {
        if (groupId === numOfGroups + 1) {
          groupId = 1
        }
        let exists = this.getAgentName(agent)
        this.nodes.push(
          _.assignIn({ id: exists.name, group: `${groupId++}` }, exists)
        )
      }
    })

    return this.applyGroupBy(this.groupByProperty || '', this.nodes)
  }

  updateLinks (links) {
    this.links.length = 0

    // filter out self connect/accept
    let uniqueConnects = _.filter(links, node => {
      return node.sourceAgent !== node.targetAgent
    })

    // filter out unique source/target sets
    uniqueConnects.map(link => {
      if (
        !_.some(this.links, {
          sourceAgent: link.sourceAgent,
          targetAgent: link.targetAgent
        })
      ) {
        let linkInfo = _.merge(link, {
          source: this.getAgentName(link.sourceAgent).name,
          target: this.getAgentName(link.targetAgent).name
        })
        this.links.push(linkInfo)
      }
    })

    return this.links
  }

  applyGroupBy (grouping, nodes) {
    let group = grouping.toLowerCase()
    switch (group) {
      case 'criticality':
        return this.groupByCriticality(nodes)
      default:
        return this.groupByNone(nodes)
    }
  }

  groupByNone (nodes) {
    this.groupBy = HOSTMAP_GROUPBY_NONE
    this.groupByProperty = undefined
    let numOfGroups = this.groupBy.length
    let groupId = 1
    let groupByNodes = _.cloneDeep(this.nodes)
    this.nodes.length = 0
    // create node object for d3 consumption
    // group, state & risklevel for color
    groupByNodes.forEach((node, idx) => {
      if (groupId === numOfGroups + 1) {
        groupId = 1
      }
      node.group = `${groupId}`
      this.clearAgentPositions(node)
      this.nodes.push(node)
      groupId++
    })

    return this.nodes
  }

  groupByCriticality (nodes) {
    if (_.isUndefined(nodes)) {
      nodes = _.cloneDeep(this.nodes)
    }
    this.groupBy = [
      CriticalityLevelEnum.REGULAR,
      CriticalityLevelEnum.IMPORTANT,
      CriticalityLevelEnum.CRITICAL
    ]
    this.groupByProperty = 'criticality'
    let groupByNodes = _.groupBy(nodes, this.groupByProperty)
    this.nodes.length = 0
    for (let criticality in groupByNodes) {
      let idx = _.indexOf(this.groupBy, criticality)
      if (idx > -1) {
        groupByNodes[criticality].forEach(node => {
          node.group = `${idx + 1}`
          this.clearAgentPositions(node)
          this.nodes.push(node)
        })
      }
    }
    return this.nodes
  }
}



// WEBPACK FOOTER //
// ./src/resources/collections/Hostmap.js